import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Link, useNavigate, useParams} from "react-router-dom";
import {
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Container,
    IconButton,
    List,
    ListItem,
    Typography,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import {useAlert} from "../context/AlertContext";
import BreadcrumbLink from "../components/BreadcrumbLink";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";

import {deletePracticeTask, getPracticeTasksList} from "../api/api";
import {PracticeTaskListResponse} from "../types";

import AddPracticeTaskModal from "../components/AddPracticeTaskModal";
import EditPracticeTaskModal from "../components/EditPracticeTaskModal";

const PracticeTasksPage = () => {
    const {t} = useTranslation();

    const {lessonId} = useParams<{ lessonId?: string }>();
    const safeLessonId = lessonId ?? "";
    const navigate = useNavigate();

    const {showAlert} = useAlert();

    const [data, setData] = useState<PracticeTaskListResponse | null>(null);
    const [loading, setLoading] = useState(false);

    const [addOpen, setAddOpen] = useState(false);

    const [editOpen, setEditOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState<any | null>(null);

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTaskId, setDeleteTaskId] = useState<number | null>(null);

    const load = async () => {
        if (!safeLessonId) return;

        try {
            setLoading(true);
            const res = await getPracticeTasksList(Number(safeLessonId));
            setData(res);
        } catch (error: any) {
            showAlert(error?.message ?? t("practicetaskslist.errors.loadFailed"), "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeLessonId]);

    const openEdit = (task: any) => {
        setSelectedTask({
            id: task.id,
            title: task.title,
            content: task.content,
            orderNum: task.orderNum,
        });
        setEditOpen(true);
    };

    const closeEdit = () => {
        setEditOpen(false);
        setSelectedTask(null);
    };

    const openDelete = (taskId: number) => {
        setDeleteTaskId(taskId);
        setDeleteOpen(true);
    };

    const closeDelete = () => {
        setDeleteOpen(false);
        setDeleteTaskId(null);
    };

    const confirmDelete = async () => {
        if (!deleteTaskId) return;

        try {
            await deletePracticeTask(deleteTaskId);
            showAlert(t("practicetaskslist.alerts.deleted"), "success");
            closeDelete();
            await load();
        } catch (e: any) {
            showAlert(e?.message ?? t("practicetaskslist.errors.deleteFailed"), "error");
        }
    };

    if (loading && !data) {
        return (
            <Container sx={{mt: 6, display: "flex", justifyContent: "center"}}>
                <CircularProgress/>
            </Container>
        );
    }

    if (!data) {
        return (
            <Container sx={{mt: 4}}>
                <Button component={Link} to="/" variant="outlined">
                    {t("common.toMainPage")}
                </Button>
            </Container>
        );
    }

    return (
        <Box sx={{px: {xs: 3, md: 3}}}>
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small"/>}
                maxItems={3}
                itemsBeforeCollapse={1}
                itemsAfterCollapse={2}
                sx={{
                    mb: 2,
                    "& .MuiBreadcrumbs-ol": {
                        overflow: "hidden",
                    },
                    "& .MuiBreadcrumbs-li": {
                        minWidth: 0,
                    },
                }}
            >
                <BreadcrumbLink to="/courses">
                    {t("nav.courses")}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/course/${data.courseId}`}>
                    {data.courseTitle}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/chapter/${data.chapterId}`}>
                    {data.chapterTitle}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/lesson/${data.lessonId}`}>
                    {data.lessonTitle}
                </BreadcrumbLink>

                <Typography color="text.primary" variant="body2">
                    <strong>{t("practicetaskslist.practiceTasks")}</strong>
                </Typography>
            </Breadcrumbs>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography sx={{fontSize: 28, fontWeight: 800}} noWrap>
                    {data.lessonTitle}
                </Typography>

                <IconButton
                    onClick={() => setAddOpen(true)}
                    aria-label={t("practicetaskslist.addPracticeTask")}
                    sx={(theme) => ({
                        mb: 2,
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        backgroundColor: theme.palette.action.hover,
                        color: theme.palette.text.primary,
                        "&:hover": {
                            backgroundColor: theme.palette.action.selected,
                        },
                    })}
                >
                    <AddIcon/>
                </IconButton>
            </Box>

            {data.taskList.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {t("practicetaskslist.noPracticeTasks")}
                </Typography>
            ) : (
                <Box
                    sx={(theme) => ({
                        mt: 3,
                        mb: 2,
                        border: `1px solid ${theme.palette.divider}`,
                    })}
                >
                    <List sx={{p: 0}}>
                        {data.taskList
                            .slice()
                            .sort((a, b) => a.orderNum - b.orderNum)
                            .map((task) => (
                                <ListItem
                                    key={task.id}
                                    sx={(theme) => ({
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                        px: 2,
                                        py: 1.5,
                                        display: "block",
                                        "&:last-child": {
                                            borderBottom: "none",
                                        },
                                    })}
                                >
                                    <Box
                                        sx={{
                                            width: "100%",
                                            display: "flex",
                                            alignItems: {xs: "stretch", sm: "center"},
                                            justifyContent: "space-between",
                                            gap: 2,
                                            flexDirection: {xs: "column", sm: "row"},
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                color: "text.primary",
                                                minWidth: 0,
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {task.orderNum}. {task.title}
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                flexShrink: 0,
                                                justifyContent: {xs: "flex-end", sm: "flex-start"},
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                sx={{
                                                    textTransform: "none",
                                                    minWidth: 96,
                                                    px: 1.5,
                                                    whiteSpace: "nowrap",
                                                }}
                                                onClick={() => navigate(`/practice-tasks/${task.id}`)}
                                            >
                                                {t("practicetaskslist.details")}
                                            </Button>

                                            <IconButton
                                                onClick={() => openEdit(task)}
                                                aria-label={t("common.edit")}
                                                size="small"
                                            >
                                                <EditIcon/>
                                            </IconButton>

                                            <IconButton
                                                onClick={() => openDelete(task.id)}
                                                aria-label={t("common.delete")}
                                                size="small"
                                            >
                                                <DeleteIcon/>
                                            </IconButton>
                                        </Box>
                                    </Box>
                                </ListItem>
                            ))}
                    </List>
                </Box>
            )}

            <AddPracticeTaskModal
                open={addOpen}
                onClose={() => setAddOpen(false)}
                lessonId={Number(safeLessonId)}
                onCreated={load}
            />

            <EditPracticeTaskModal
                open={editOpen}
                onClose={closeEdit}
                task={selectedTask}
                onUpdated={load}
            />

            <DeleteConfirmationModal
                open={deleteOpen}
                onClose={closeDelete}
                onConfirm={confirmDelete}
            />
        </Box>
    );
};

export default PracticeTasksPage;